import { useState } from 'react';
import type { PlaceSnapshot, ScoreMetricResult } from '@/types/gmb';
import { formatNumber } from '@/pages/gmb-audit/utils/format';
import {
  formatMetricDisplayLabel,
  formatMetricReasonForDisplay,
  formatPriorityTitle,
  metricStatusText,
  metricStatusTone,
} from '@/pages/gmb-audit/utils/reportDisplay';
import {
  buildDiagnosisDimensions,
  DIMENSION_DEFS,
  type DiagnosisDimension,
} from '@/pages/gmb-audit/utils/diagnosisDimensions';

// UI-2B｜11 項健診明細 → 4 組 Accordion（純顯示層）。
// 分組沿用 UI-2A 的 4 大構面；只呈現既有 Backend Metric，
// 不重算分數、不重算 threshold、不影響 Score / Grade / Priority。
// 初次載入全部 collapsed；HIGH priority 指標僅加 badge，不自動展開。

const TONE_CLASS: Record<'good' | 'warn' | 'bad' | 'na', string> = {
  good: 'bg-primary-100 text-primary-900 ring-1 ring-primary-200',
  warn: 'bg-accent-100 text-accent-900 ring-1 ring-accent-200',
  bad: 'bg-accent-200 text-accent-900 ring-1 ring-accent-300',
  na: 'bg-secondary-100 text-secondary-900 ring-1 ring-secondary-200',
};

function barColor(ratio: number): string {
  if (ratio >= 0.7) return 'bg-primary-600';
  if (ratio >= 0.4) return 'bg-accent-500';
  return 'bg-accent-600';
}

/** 目前資料值 → 人類可讀文字（不改變原始值）。 */
function formatMetricValue(metric: ScoreMetricResult): string {
  const v = metric.value;
  if (v === undefined || v === null) return '—';
  if (typeof v === 'boolean') return v ? '是' : '否';
  if (typeof v === 'number') return formatNumber(v);
  return String(v);
}

/** 構面層級的簡短狀態文案。 */
function dimensionStatusText(dimension: DiagnosisDimension): string {
  if (dimension.availableMax <= 0) return '資料不足';
  if (dimension.hasInsufficientPeerData) return '同業資料不足';
  if (dimension.hasInsufficientData) return '部分資料不足';
  if (dimension.completionRate >= 0.8) return '大致完整';
  if (dimension.completionRate >= 0.6) return '仍算穩定';
  if (dimension.completionRate >= 0.4) return '仍有缺口';
  return '明顯不足';
}

function MetricRow({ metric }: { metric: ScoreMetricResult }) {
  const [open, setOpen] = useState(false);
  const tone = metricStatusTone(metric);
  const scored = metric.status === 'SCORED' && typeof metric.score === 'number';
  const ratio =
    scored && metric.maxScore > 0 ? Math.max(0, Math.min(1, (metric.score as number) / metric.maxScore)) : 0;
  const suggestion = formatPriorityTitle(metric.key, '');

  return (
    <div className="rounded-xl border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-bold text-foreground-950">
              {formatMetricDisplayLabel(metric.key, metric.label)}
            </span>
            {metric.priority === 'HIGH' && (
              <span className="ds-badge bg-accent-100 text-accent-900 ring-1 ring-accent-200">
                <i className="ri-arrow-up-line" />
                高優先
              </span>
            )}
          </div>
          {scored && (
            <div className="mt-2 h-2 w-full max-w-[220px] overflow-hidden rounded-full bg-background-200">
              <div className={`h-full rounded-full ${barColor(ratio)}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2.5">
          {scored ? (
            <span className="whitespace-nowrap text-lg font-bold leading-none text-primary-800">
              {metric.score}
              <span className="text-sm font-medium text-foreground-400"> / {metric.maxScore}</span>
            </span>
          ) : (
            <span className={`ds-badge ${TONE_CLASS[tone]}`}>{metricStatusText(metric)}</span>
          )}
          {scored && <span className={`ds-badge ${TONE_CLASS[tone]}`}>{metricStatusText(metric)}</span>}
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-foreground-400`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-background-200 px-4 pb-4 pt-3.5">
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-foreground-500">目前資料</dt>
              <dd className="mt-0.5 font-semibold text-foreground-900">{formatMetricValue(metric)}</dd>
            </div>
            <div>
              <dt className="text-foreground-500">判定依據</dt>
              <dd className="mt-0.5 leading-relaxed text-foreground-700">
                {formatMetricReasonForDisplay(metric.reason)}
              </dd>
            </div>
            {typeof metric.benchmark === 'number' && (
              <div>
                <dt className="text-foreground-500">同業對照值</dt>
                <dd className="mt-0.5 font-semibold text-foreground-900">{formatNumber(metric.benchmark)}</dd>
              </div>
            )}
            {suggestion && (
              <div>
                <dt className="text-foreground-500">改善建議</dt>
                <dd className="mt-0.5 leading-relaxed text-foreground-700">{suggestion}</dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

function DimensionAccordion({ dimension, metrics }: { dimension: DiagnosisDimension; metrics: ScoreMetricResult[] }) {
  const [open, setOpen] = useState(false);
  const hasData = dimension.availableMax > 0;
  const ratePct = hasData ? Math.round(dimension.completionRate * 100) : 0;

  return (
    <div className="rounded-2xl border border-background-300 bg-background-100 shadow-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h3 className="text-lg text-foreground-950">{dimension.title}</h3>
            <span className="ds-badge bg-secondary-100 text-secondary-900">{dimensionStatusText(dimension)}</span>
          </div>
          <p className="mt-0.5 text-sm text-foreground-500">{dimension.caption}</p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {hasData && (
            <span className="text-lg font-bold text-foreground-950">
              {dimension.earned}
              <span className="text-sm font-medium text-foreground-400"> / {dimension.availableMax}</span>
            </span>
          )}
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-2xl text-foreground-400`} />
        </div>
      </button>

      {open && (
        <div className="space-y-2.5 border-t border-background-200 px-4 pb-4 pt-4 md:px-5">
          {metrics.map((metric) => (
            <MetricRow key={metric.key} metric={metric} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 11 項健診明細（4 組 Accordion）。
 * 唯一資料來源為 scoreResult.metrics[]；沿用 Backend 原順序（M01～M11）。
 */
export default function ReportMetricsSection({
  metrics,
  target,
}: {
  metrics: ScoreMetricResult[];
  target: PlaceSnapshot;
}) {
  // target 保留於簽章以維持呼叫端一致（本批 Accordion 不再需要額外標注）。
  void target;

  const list = metrics || [];
  const metricMap = new Map<string, ScoreMetricResult>();
  list.forEach((m) => metricMap.set(m.key, m));

  const dimensions = buildDiagnosisDimensions(list);
  const dimMap = new Map<string, DiagnosisDimension>();
  dimensions.forEach((d) => dimMap.set(d.key, d));

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-list-check-2 text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">11 項健診明細</h2>
      </div>
      <p className="mb-6 text-base text-foreground-500">
        依 4 大構面分組，點開即可查看每項的目前資料、判定依據與改善建議。標示為資料不足的項目不會以 0 分呈現。
      </p>

      <div className="space-y-3.5">
        {DIMENSION_DEFS.map((def) => {
          const dimension = dimMap.get(def.key);
          if (!dimension) return null;
          const groupMetrics = def.metricKeys
            .map((key) => metricMap.get(key))
            .filter((m): m is ScoreMetricResult => Boolean(m));
          return <DimensionAccordion key={def.key} dimension={dimension} metrics={groupMetrics} />;
        })}
      </div>
    </section>
  );
}